import { ContentExtrasBar } from '@/components/content/ContentExtrasBar';
import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  SkipBack, 
  SkipForward,
  CheckCircle,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  Image as ImageIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface InfographicModalProps {
  isOpen: boolean;
  contentId?: string;
  onClose: () => void;
  lesson: {
    title: string;
    description: string;
    duration: string;
  };
  imageUrl?: string;
  audioUrl?: string;
  onComplete: () => void;
  isCompleted: boolean;
}

const placeholderImage = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=1600&fit=crop';

export function InfographicModal({
  contentId,
  isOpen,
  onClose,
  lesson,
  imageUrl,
  audioUrl,
  onComplete,
  isCompleted
}: InfographicModalProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [hasListened, setHasListened] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const displayImage = imageUrl || placeholderImage;

  useEffect(() => {
    if (!isOpen) {
      setIsPlaying(false);
      setCurrentTime(0);
      setZoom(1);
      setIsFullscreen(false);
    }
  }, [isOpen]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
        setHasListened(true);
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) setDuration(audioRef.current.duration);
  };

  const handleSeek = (value: number[]) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);
    if (audioRef.current) audioRef.current.volume = newVolume;
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  const skipBack = () => {
    if (audioRef.current) audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
  };

  const skipForward = () => {
    if (audioRef.current) audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10);
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
    if (!isCompleted) onComplete();
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const zoomIn = () => setZoom(prev => Math.min(prev + 0.25, 2));
  const zoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const resetZoom = () => setZoom(1);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn(
        "p-0 overflow-hidden bg-card",
        isFullscreen
          ? "max-w-[100vw] w-screen h-screen max-h-screen rounded-none"
          : "max-w-4xl max-h-[90vh]"
      )}>
        <DialogHeader className="p-4 pb-2 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold">{lesson.title}</DialogTitle>
                <p className="text-sm text-muted-foreground">{lesson.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isCompleted && (
                <Badge className="bg-success text-white gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Completado
                </Badge>
              )}
              <Badge variant="outline">{lesson.duration}</Badge>
              <button
                onClick={() => setIsFullscreen(f => !f)}
                className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
                title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4 text-muted-foreground" /> : <Maximize2 className="w-4 h-4 text-muted-foreground" />}
              </button>
            </div>
          </div>
        </DialogHeader>
        {contentId && <ContentExtrasBar contentId={contentId} description={lesson.description} />}

        {/* Infographic Viewer */}
        <div className={cn("relative flex-1 overflow-auto bg-muted/30", isFullscreen ? '' : 'max-h-[50vh]')}>
          {/* Zoom Controls */}
          <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-card/90 backdrop-blur-sm rounded-lg p-1 border border-border">
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={zoomOut}>
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-xs font-medium px-2 min-w-[50px] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={zoomIn}>
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={resetZoom}>
              <Maximize2 className="w-4 h-4" />
            </Button>
          </div>

          {/* Infographic Image */}
          <div className="flex items-center justify-center p-4 min-h-[300px]">
            <img
              src={displayImage}
              alt={lesson.title}
              className="max-w-full h-auto rounded-lg shadow-lg transition-transform duration-300"
              style={{ transform: `scale(${zoom})` }}
            />
          </div>
        </div>

        {/* Audio Player — only if audioUrl provided */}
        {audioUrl ? (
          <div className="p-4 bg-gradient-to-t from-card to-card/95 border-t border-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Volume2 className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Explicación en audio</p>
                <p className="text-xs text-muted-foreground">Escucha mientras observas la infografía</p>
              </div>
            </div>

            <audio
              ref={audioRef}
              src={audioUrl}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleAudioEnd}
            />

            {/* Progress Bar */}
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs text-muted-foreground w-10 text-right">
                {formatTime(currentTime)}
              </span>
              <Slider
                value={[currentTime]}
                max={duration || 100}
                step={1}
                onValueChange={handleSeek}
                className="flex-1"
              />
              <span className="text-xs text-muted-foreground w-10">
                {formatTime(duration)}
              </span>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button size="icon" variant="ghost" className="h-9 w-9" onClick={toggleMute}>
                  {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </Button>
                <Slider
                  value={[isMuted ? 0 : volume]}
                  max={1}
                  step={0.1}
                  onValueChange={handleVolumeChange}
                  className="w-24"
                />
              </div>

              <div className="flex items-center gap-2">
                <Button size="icon" variant="ghost" className="h-10 w-10" onClick={skipBack}>
                  <SkipBack className="w-5 h-5" />
                </Button>
                <Button
                  size="icon"
                  className="h-12 w-12 rounded-full bg-primary"
                  onClick={togglePlay}
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5 text-primary-foreground" />
                  ) : (
                    <Play className="w-5 h-5 text-primary-foreground ml-0.5" />
                  )}
                </Button>
                <Button size="icon" variant="ghost" className="h-10 w-10" onClick={skipForward}>
                  <SkipForward className="w-5 h-5" />
                </Button>
              </div>

              <div className="w-[120px] flex justify-end">
                {!isCompleted && hasListened && (
                  <Button size="sm" onClick={onComplete} className="gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Completar
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* No audio — show complete button directly */
          <div className="p-4 border-t border-border flex justify-end">
            {!isCompleted && (
              <Button size="sm" onClick={onComplete} className="gap-1">
                <CheckCircle className="w-4 h-4" />
                Completar
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
